<template>
  <nuxt-link
    v-if="!!userNotification?.client"
    class="group -mx-2 flex cursor-pointer items-center rounded-md px-2 py-3 hover:bg-gray-100"
    @click="emit('clickAction')"
  >
    <WrenchIcon
      v-if="!!userNotification.updatedBySystem"
      class="size-8 shrink-0 rounded-full bg-gray-600 p-2 text-white ring-8 ring-white group-hover:ring-gray-100 dark:ring-slate-800"
    />
    <UserAvatar
      v-else
      :user="userNotification.createdBy"
      :has-popover="false"
      size="8"
      class="shrink-0"
    />
    <p class="grow px-2">
      <span class="text-wrap text-gray-900 group-hover:underline">
        <span class="font-medium">
          {{ `[${userNotification.rolloutIdentifier}]` }}
        </span>
        {{ message }}
      </span>
      <br />
      <span class="text-xs text-gray-500">
        {{ formattedDate }}
      </span>
    </p>

    <input
      type="radio"
      :checked="!isRead"
      class="size-5 shrink-0 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-600"
      :title="$t('user-notifications.mark-as-' + (!isRead ? 'read' : 'unread'))"
      @click.stop="updateNotificationViewed"
    />
  </nuxt-link>
</template>

<script lang="ts" setup>
import { WrenchIcon } from '@heroicons/vue/24/solid';
import { useUserStore } from '../../stores/user';
import { formatToDifference } from '@/util/formatter/date';
import type { UserNotificationResponseItem } from '~/composables/api-services/api-notfication';
import { UserHistoryOperation } from '~/types';
import { useUserNotificationsStore } from '~/stores/user-notification';

const props = defineProps({
  userNotification: {
    type: Object as PropType<UserNotificationResponseItem>,
    required: true,
  },
});

const emit = defineEmits(['clickAction', 'updated']);

const i18n = useI18n();

const operationMessageMap: Partial<Record<UserHistoryOperation, string>> = {
  [UserHistoryOperation.ASSIGN_TO]: i18n.t(
    props.userNotification.updatedBySystem
      ? 'user-notifications.system_assign_to'
      : 'user-notifications.assign_to',
    {
      createdBy: props.userNotification.createdBy?.name,
      clientName: props.userNotification.client?.name,
    }
  ),
  [UserHistoryOperation.UN_ASSIGN_FROM]: i18n.t(
    props.userNotification.updatedBySystem
      ? 'user-notifications.system_un_assign_from'
      : 'user-notifications.un_assign_from',
    {
      createdBy: props.userNotification.createdBy?.name,
      clientName: props.userNotification.client?.name,
    }
  ),
  [UserHistoryOperation.UPDATE_CLIENT_USER]: !props.userNotification.to
    ?.clientUser
    ? i18n.t('user-notifications.remove_client_user', {
      createdBy: props.userNotification.createdBy?.name,
      clientName: props.userNotification.client?.name,
    })
    : i18n.t('user-notifications.update_client_user', {
      createdBy: props.userNotification.createdBy?.name,
      clientName: props.userNotification.client?.name,
      clientUserName: props.userNotification.to?.clientUser?.name,
    }),
  [UserHistoryOperation.COMMENT_MENTIONED]: i18n.t(
    'user-notifications.comment_mentioned',
    {
      clientName: props.userNotification.client?.name,
      authorName: props.userNotification.createdBy?.name,
    }
  ),
  [UserHistoryOperation.CLIENT_TASK_CREATE]: i18n.t(
    'user-notifications.client_task_create',
    {
      clientName: props.userNotification.client?.name,
      taskCreatorName: props.userNotification.createdBy?.name,
    }
  ),
  [UserHistoryOperation.ASSIGN_TO_TASK]: i18n.t(
    'user-notifications.assign_to_task',
    {
      taskName: props.userNotification.clientTask?.name,
      taskCreatorName: props.userNotification.createdBy?.name,
    }
  ),
  [UserHistoryOperation.UN_ASSIGN_FROM_TASK]: i18n.t(
    'user-notifications.un_assign_from_task',
    {
      taskName: props.userNotification.clientTask?.name,
      taskCreatorName: props.userNotification.createdBy?.name,
    }
  ),
};

const formattedDate = computed(() => {
  const date = props.userNotification.created?.toString();
  return formatToDifference(date, i18n);
});

const message = computed(() => {
  return operationMessageMap[props.userNotification.operation] ?? '';
});

// Read status

const userNotificationsStore = useUserNotificationsStore();
const userStore = useUserStore();

const isRead = computed(
  () =>
    props.userNotification.users.find((user) => {
      return user.user?._id === userStore.user?._id.toString();
    })?.isViewed ?? false
);

const updateNotificationViewed = async () => {
  await userNotificationsStore.updateNotificationViewedStatus(
    [props.userNotification._id],
    !isRead.value
  );

  emit('updated');
};
</script>
